import {
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  CircularProgress,
  FormControlLabel,
  Switch,
  Alert,
  Button,
} from "@mui/material";
import { useState } from "react";
import { useContentSetting } from "src/Hooks/ContentContext/ContentSettingState";
import {
  _get_is_seen_video_localStorage,
  _get_user_from_localStorage,
  _get_user_from_localStorage_new,
} from "src/DAL/localStorage/localStorage";
import { useEffect } from "react";
import { EditProfileApiDashBoard, memberDetail } from "src/DAL/Profile/Profile";
import { makeStyles } from "@mui/styles";
import { useSnackbar } from "notistack";
import SummaryCard from "src/components/_dashboard/app/SummaryCard";
import { useNavigate } from "react-router-dom";
import WeightWaistChart from "src/components/WeightWaistChart";
import MainPart from "./CommunityFeeds/MainPart";
import WorkOutList from "./WorkOutList";
import PaceCalculator from "./PaceCalculator";
import {
  CALCULATE_VDOT_VALUE,
  GET_VDOT,
  paceFromVdot,
} from "src/utils/constants";
import CustomPopoverNew from "src/components/CustomPopoverNew";
import AddWeight from "./WeightTracker/AddWeight";
import RecordNotFound from "src/components/GeneralComponents/RecordNotFound";
import EditProfile from "./Profile/EditProfile";
import { get_harmony_ring_information_api } from "src/DAL/Programmes/Programmes";
import RadialBarChart from "./Programmes/RadialBarChart";
import moment from "moment";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";

const useStyles = makeStyles(() => ({
  loading: {
    marginLeft: "50%",
    marginTop: "20%",
  },
}));

const findPaceObjectByVdot = (vdotValue) => {
  return paceFromVdot.find((paceObject) => paceObject.vdot == vdotValue);
};

const useMemberData = (programId) => {
  return useQuery({
    queryKey: ["memberData", programId],
    queryFn: async () => {
      let id = programId !== "all" ? programId : "";
      const result = await memberDetail(id);
      if (result.code === 200) {
        return { ...result, ...result?.member };
      }
      throw new Error(result.message);
    },
  });
};

const useHarmonyRingData = (programId, userId, enabled = false) => {
  const getTodayDate = () => new Date();
  const getOneWeekAgoDate = () => {
    const date = new Date();
    date.setDate(date.getDate() - 7);
    return date;
  };

  return useQuery({
    queryKey: ["harmonyRingData", programId, userId],
    queryFn: async () => {
      const postData = {
        start: moment(getOneWeekAgoDate()).format("YYYY-MM-DD"),
        end: moment(getTodayDate()).format("YYYY-MM-DD"),
      };

      const result = await get_harmony_ring_information_api(
        programId,
        userId,
        postData.start,
        postData.end,
      );

      if (result.code === 200) {
        return result.harmony_ring_date;
      }
      throw new Error(result.message);
    },
    enabled: enabled && !!programId && !!userId,
  });
};

export default function DashboardApp() {
  const navigate = useNavigate();
  const { userInfo } = useContentSetting();
  const { contentSettingData, dashboardSettingData } = useContentSetting();
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();

  const [checkedPace, setCheckedPace] = useState(true);
  const [showInfoBox, setShowInfoBox] = useState(false);
  const [isOpenInfo, setIsOpenInfo] = useState(false);
  const [dummyData, setDummyData] = useState([]);
  const [harmonyProgram, setHarmonyProgram] = useState([]);
  const [isShow, setIsShow] = useState(false);
  const [openWeight, setOpenWeight] = useState(false);
  const [userPre, setUserPre] = useState({});
  const [selectProgram, setSelectProgram] = useState("all");
  const [selectHaromny, setSelectHaromny] = useState("");
  const [chartList, setChartList] = useState([]);
  const [regularDayCalories, setRegularDayCalories] = useState(0);
  const [memberVdotData, setMemberVdotDataData] = useState({
    time_interval: 0,
    distance: "5km",
    easy_long: "e_km",
    thre_shold: "t_km",
    interval: "i_400_m",
  });

  const {
    data: memberData = {},
    isLoading: isLoadingMember,
    isError: memberError,
    error: memberErrorData,
  } = useMemberData(selectProgram);

  const {
    data: programmesData = [],
    isLoading: isHarmonyLoading,
    isError: harmonyError,
    error: harmonyErrorData,
  } = useHarmonyRingData(selectHaromny, userInfo?._id, !!selectHaromny);

  const updateProfileMutation = useMutation({
    mutationFn: async (formData) => {
      const result = await EditProfileApiDashBoard(formData, "dashboard");
      if (result.code === 200) {
        return result;
      }
      throw new Error(result.message);
    },
    onSuccess: (result) => {
      localStorage.setItem(`user_data`, JSON.stringify(result?.member));
      localStorage.setItem(`user_data_new`, JSON.stringify(result?.member));
      enqueueSnackbar("Pace calculator updated successfully", {
        variant: "success",
      });

      setMemberVdotDataData((inputs) => ({
        ...inputs,
        ["distance"]: checkedPace ? result?.member?.vdot?.race_type : "5km",
        ["time_interval"]: parseInt(
          result?.member?.vdot?.time_taken
            ? checkedPace
              ? result?.member?.vdot?.time_taken
              : 0
            : 0,
        ),
      }));
      setIsShow(false);

      queryClient.invalidateQueries(["memberData"]);
    },
    onError: (error) => {
      enqueueSnackbar(error.message, { variant: "error" });
    },
  });

  let vdot = {};

  let vdot_parsed = {
    race_type: memberVdotData?.distance,
    time_taken: checkedPace ? memberVdotData?.time_interval : 0,
  };

  if (vdot_parsed.race_type && vdot_parsed.time_taken) {
    vdot = {
      race_type: vdot_parsed.race_type,
      time_taken: vdot_parsed.time_taken,
      distance_unit: "km",
    };
    let sorted_vdots = GET_VDOT;
    let race_type = "marathon";
    if (vdot_parsed.race_type == "marathon") {
      vdot.distance = 42.195;
      race_type = "marathon";
      sorted_vdots = GET_VDOT.sort((a, b) => a.marathon - b.marathon);
    } else if (vdot_parsed.race_type == "half_marathon") {
      vdot.distance = 21.098;
      race_type = "half_marathon";
      sorted_vdots = GET_VDOT.sort((a, b) => a.half_marathon - b.half_marathon);
    } else if (vdot_parsed.race_type == "5km") {
      vdot.distance = 5;
      race_type = "5km";
      sorted_vdots = GET_VDOT.sort((a, b) => a["5km"] - b["5km"]);
    } else if (vdot_parsed.race_type == "10km") {
      vdot.distance = 10;
      race_type = "10km";
      sorted_vdots = GET_VDOT.sort((a, b) => a["10km"] - b["10km"]);
    } else if (vdot_parsed.race_type == "15km") {
      vdot.distance = 15;
      race_type = "15km";
      sorted_vdots = GET_VDOT.sort((a, b) => a["15km"] - b["15km"]);
    }

    vdot.vdot_value = CALCULATE_VDOT_VALUE(
      race_type,
      vdot_parsed.time_taken,
      sorted_vdots,
    );
  }

  const vdot33Object = findPaceObjectByVdot(
    vdot.vdot_value ? vdot.vdot_value : 0,
  );

  const handleNavigate = () => {
    const selectedProgram = harmonyProgram.find(
      (program) => program.program_id === selectHaromny,
    );
    if (!selectedProgram) return;
    navigate(`/programmes/calendar/${selectedProgram.program_slug}`, {
      state: {
        ...selectedProgram,
        workout_title: selectedProgram.programme_title,
        title: selectedProgram.programme_title,
        _id: {
          _id: selectedProgram.program_id,
          is_harmony_ring_enabled: true,
        },
        open_dashboard: false,
      },
    });
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    let VDOT = {
      race_type: checkedPace ? memberVdotData?.distance : "5km",
      time_taken: checkedPace ? memberVdotData?.time_interval : 0,
    };

    const formData = new FormData();
    formData.append("pace_calculator", checkedPace);
    formData.append("vdot", JSON.stringify(VDOT));

    if (checkedPace && memberVdotData?.time_interval == 0) {
      return enqueueSnackbar("Please enter a value in the Duration field.", {
        variant: "error",
      });
    }

    updateProfileMutation.mutate(formData);
  };

  const handleChange = (event) => {
    setSelectProgram(event.target.value);
  };

  const handleChangeHaromny = (event) => {
    setSelectHaromny(event.target.value);
  };

  useEffect(() => {
    if (memberError) {
      enqueueSnackbar(memberErrorData?.message, { variant: "error" });
    }
  }, [memberError, memberErrorData, enqueueSnackbar]);

  useEffect(() => {
    if (harmonyError) {
      enqueueSnackbar(harmonyErrorData?.message, { variant: "error" });
    }
  }, [harmonyError, harmonyErrorData, enqueueSnackbar]);

  useEffect(() => {
    if (memberData && Object.keys(memberData).length > 0) {
      setChartList(memberData?.graph_weights || []);
      setUserPre(memberData?.user_preferences || {});

      let isCheck = memberData?.pace_calculator;
      setMemberVdotDataData((inputs) => ({
        ...inputs,
        ["distance"]: isCheck ? memberData?.vdot?.race_type : "5km",
        ["time_interval"]: parseInt(
          memberData?.vdot?.time_taken
            ? isCheck
              ? memberData?.vdot?.time_taken
              : 0
            : 0,
        ),
      }));
      setCheckedPace(isCheck);

      const harmonyProgrammes =
        memberData?.programme_array?.filter(
          (item) => item.is_harmony_ring_enabled === true,
        ) || [];
      setHarmonyProgram(harmonyProgrammes);

      if (harmonyProgrammes.length > 0 && !selectHaromny) {
        setSelectHaromny(harmonyProgrammes[0]?.program_id);
      }
    }
  }, [memberData, selectHaromny]);

  useEffect(() => {
    const requiredFields = [
      "first_name",
      "last_name",
      "email",
      "dob",
      "street",
      "state",
      "city",
      "country",
      "zip_code",
      "time_zone",
      "gender",
      "weight",
      "height",
      "age",
      "activity_level",
    ];

    let isAnyFieldMissing = requiredFields.some((field) => !userInfo?.[field]);

    if (userInfo?.gender === "female" && !userInfo?.last_cycle_date) {
      isAnyFieldMissing = true;
    }
    if (
      userInfo?.deficit_percentage &&
      !userInfo?.deficit_value &&
      !userInfo?.days_for_deficit
    ) {
      isAnyFieldMissing = true;
    }

    if (isAnyFieldMissing) {
      setIsOpenInfo(true);
    } else {
      setIsOpenInfo(false);
    }
  }, [userInfo]);

  useEffect(() => {
    setMemberVdotDataData((prevState) => {
      return {
        ...prevState,
        ["pace"]: vdot33Object
          ? vdot33Object[prevState?.easy_long + "_low"]
          : null,
        ["paceUp"]: vdot33Object
          ? vdot33Object[prevState?.easy_long + "_up"]
          : null,
        ["pace1"]: vdot33Object ? vdot33Object[prevState?.thre_shold] : null,
        ["pace2"]: vdot33Object ? vdot33Object[prevState?.interval] : null,
      };
    });
  }, [
    memberVdotData?.easy_long,
    memberVdotData?.thre_shold,
    memberVdotData?.interval,
    vdot?.vdot_value,
  ]);
  const storedUserInfo = _get_user_from_localStorage();

  useEffect(() => {
    const caloriesValue = Number(storedUserInfo?.regular_day_calories);
    setRegularDayCalories(Number.isFinite(caloriesValue) ? caloriesValue : 0);
  }, [storedUserInfo]);

  useEffect(() => {
    setDummyData([
      {
        color: "#fff",
        title: "Total Coins",
        tooltip: "Total Coins",
        count: memberData?.total_points ? memberData?.total_points : 0,
        icon: "mdi:currency-usd-circle",
        link: true,
        to: `/dashboard-coins-info`,
      },
      {
        color: "#fff",
        title: "Total Daily Coins (AP)",
        tooltip: "Total Daily Coins (Accountability Partner)",
        count: memberData?.reward_points?.today_coins
          ? memberData?.reward_points?.today_coins
          : 0,
        icon: "mdi:cash-multiple",
        link: false,
      },
      {
        color: "#fff",
        title: "",
        count: "Shopping List",
        tooltip: "Shopping List",
        icon: "mdi:cart-outline",
        link: true,
        link_text: "Create Shopping List →",
        to: `/dashboard/shopping-list`,
      },
    ]);
  }, [memberData, regularDayCalories]);

  if (isLoadingMember) {
    return <CircularProgress className={classes.loading} color="primary" />;
  }

  return (
    <div className="container-fluid">
      <div className="row ">
        <div className="col-12 col-md-4 dashboard_description">
          {contentSettingData && contentSettingData.dashboard_description ? (
            <div
              dangerouslySetInnerHTML={{
                __html: contentSettingData.dashboard_description,
              }}
            ></div>
          ) : (
            <h2 className="dashboard-heading">
              {dashboardSettingData?.wellcome_text}
            </h2>
          )}
        </div>{" "}
        <div className="col-12 col-md-8 mt-3 mt-md-0">
          {memberData?.gender === "female" && memberData?.is_menstrual_cycle ? (
            <Alert
              sx={{
                textAlign: "left",
                width: "100%",
                backgroundColor: "#FF7F00",
                color: "#fff",
                "& .MuiAlert-icon": {
                  color: "#fff",
                },
              }}
              severity="warning"
            >
              Gentle Reminder: You’re on your menstrual cycle — it’s completely
              normal to feel less strong during this time. Please listen to your
              body, take it easy, and allow yourself to rest if needed. Your
              well-being comes first.
            </Alert>
          ) : (
            ""
          )}
        </div>
        {dummyData.map((item, i) => (
          <div key={i} className="col-12 col-lg-4 mt-3">
            <SummaryCard item={item} />
          </div>
        ))}
        {harmonyProgram && harmonyProgram.length > 0 && (
          <div className="col-12 mt-3">
            <div className="card p-4 border-0 d-flex flex-column h-100 shoping_card">
              <div className="d-flex flex-wrap justify-content-between align-items-center mb-3">
                <div>
                  <h3>Your Harmony Score</h3>
                  <p className="text-muted mb-4 mb-md-0">
                    The graph shows your task completion data from the past 7
                    days.
                  </p>
                </div>
                <div className="d-flex flex-wrap align-items-center">
                  <Button
                    onClick={handleNavigate}
                    variant="contained"
                    className="me-2 mb-3 mb-md-0"
                  >
                    Go to Programme
                  </Button>
                  <Box sx={{ width: 220 }}>
                    <FormControl fullWidth size="small">
                      <InputLabel id="demo-simple-select-label">
                        Filter By
                      </InputLabel>
                      <Select
                        labelId="demo-simple-select-label"
                        id="demo-simple-select"
                        value={selectHaromny}
                        onChange={handleChangeHaromny}
                        label="Filter By"
                      >
                        {harmonyProgram.map((item) => (
                          <MenuItem
                            key={item.program_id}
                            value={item.program_id}
                          >
                            {item.programme_title}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>
                </div>
              </div>
              <RadialBarChart
                programmesData={programmesData}
                is_show_dashbord={true}
                isHarmonyLoading={isHarmonyLoading}
              />
            </div>
          </div>
        )}
        <div className="col-12 col-md-7 mt-3">
          <div className="card p-4 border-0 d-flex flex-column h-100 shoping_card">
            <div className="d-flex justify-content-between mb-3">
              <div>
                <h3>Weight Tracker</h3>
                <p className="text-muted mb-0">
                  Your Last 30-Day Progress Summary
                </p>
              </div>
              <>
                <div className="go_to_community text-end p-0">
                  <span
                    className="pointer"
                    onClick={() => {
                      navigate(`/weight-tracker`);
                    }}
                  >
                    Go To Weight Tracker →
                  </span>
                </div>
              </>
            </div>
            <WeightWaistChart chartList={chartList} userPre={userPre} />
          </div>
        </div>
        <div className="col-12 col-md-5 d-flex flex-column">
          <div className="card border-0 flex-fill mt-3 shoping_card">
            <div className="go_to_community text-end">
              <span
                className="pointer"
                onClick={() => {
                  navigate(`/community-area`);
                }}
              >
                Go To Community Feed →
              </span>
            </div>
            <MainPart
              showInfoBox={showInfoBox}
              setShowInfoBox={setShowInfoBox}
              userInfo={userInfo}
              event_id=""
              dashboard={true}
            />
          </div>
        </div>
        <div className="col-12 col-md-7 mt-3">
          <div className="card p-4 border-0 shoping_card workout-container-fix">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h3>Daily Action Plan (DAP)</h3>
              <Box sx={{ width: 200 }}>
                <FormControl fullWidth size="small">
                  <InputLabel id="demo-simple-select-label">
                    Filter By
                  </InputLabel>
                  <Select
                    labelId="demo-simple-select-label"
                    id="demo-simple-select"
                    value={selectProgram}
                    onChange={handleChange}
                    label="Filter By"
                  >
                    <MenuItem value="all">ALL</MenuItem>
                    {memberData?.programme_array &&
                      memberData?.programme_array.length > 0 &&
                      memberData?.programme_array.map((item) => (
                        <MenuItem key={item.program_id} value={item.program_id}>
                          {item.programme_title}
                        </MenuItem>
                      ))}
                  </Select>
                </FormControl>
              </Box>
            </div>
            {memberData?.today_workout &&
            memberData?.today_workout?.length > 0 ? (
              <>
                {isLoadingMember ? (
                  <div className="text-center mt-5">
                    <CircularProgress color="primary" />
                  </div>
                ) : (
                  <div className="workout-container">
                    {memberData?.today_workout.map((item, index) => {
                      let extra = item?.exercises
                        .map((item) => {
                          if (item.is_extra) {
                            return item;
                          }
                          return [];
                        })
                        .flat();
                      return (
                        <WorkOutList key={index} item={item} extra={extra} />
                      );
                    })}
                  </div>
                )}
              </>
            ) : (
              <RecordNotFound
                title={
                  "No tasks for today! Have a great day—stay healthy and happy!"
                }
              />
            )}
          </div>
        </div>
        <div className="col-12 col-md-5 d-flex flex-column">
          <div className="card p-4 border-0 mt-3 shoping_card flex-fill">
            <div className="d-flex align-items-center justify-content-between">
              <h3>Pace Calculator</h3>
              <>
                <FormControlLabel
                  className="me-0"
                  control={
                    <Switch
                      checked={checkedPace}
                      onChange={(e) => {
                        setCheckedPace(e.target.checked);
                        setIsShow(true);
                      }}
                    />
                  }
                />
              </>
            </div>
            <p className="text-muted mb-0">
              If running is part of your programme, get your current paces by
              entering a recent time trial result. These will show automatically
              inside your programme.
            </p>

            <PaceCalculator
              checkedPace={checkedPace}
              memberVdotData={memberVdotData}
              setMemberVdotDataData={setMemberVdotDataData}
              handleUpdate={handleUpdate}
              isFormLoading={updateProfileMutation.isLoading}
              setIsShow={setIsShow}
              isShow={isShow}
            />
          </div>
        </div>
      </div>
      <CustomPopoverNew
        isOpenPop={openWeight}
        isClosePop={setOpenWeight}
        title={"Check In"}
        componentToPassDown={
          <AddWeight
            openWeight={openWeight}
            setOpenWeight={setOpenWeight}
            weight_list={() => queryClient.invalidateQueries(["memberData"])}
            editValues={null}
            userPre={userPre}
          />
        }
      />
      <CustomPopoverNew
        isOpenPop={isOpenInfo}
        title={"Update Profile"}
        is_hide_icon={true}
        componentToPassDown={
          <EditProfile isOpenInfo={isOpenInfo} setIsOpenInfo={setIsOpenInfo} />
        }
      />
    </div>
  );
}
